import { useEffect, useRef, useCallback } from 'react';
import { getAuthToken } from '@/lib/api-client';

const HEARTBEAT_INTERVAL_MS = 30_000;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

/**
 * Sends periodic heartbeats to keep session duration tracking accurate
 * for incomplete sessions. Also fires a final beacon on page unload.
 *
 * @param sessionId - Active session ID (null to disable)
 * @param apiPath - Heartbeat API path template, e.g. '/sessions/{id}/heartbeat'
 *                  The literal '{id}' will be replaced with the sessionId.
 */
export function useSessionHeartbeat(
  sessionId: string | null,
  apiPath: string,
  heartbeatFn: (sessionId: string) => Promise<void>,
) {
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const sendHeartbeat = useCallback(() => {
    if (!sessionIdRef.current) return;
    heartbeatFn(sessionIdRef.current).catch(() => {});
  }, [heartbeatFn]);

  useEffect(() => {
    if (!sessionId) return;

    const intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    const beaconUrl = `${API_BASE_URL}${apiPath.replace('{id}', sessionId)}`;

    const sendBeaconHeartbeat = () => {
      if (!sessionIdRef.current) return;
      const token = getAuthToken();
      try {
        fetch(beaconUrl, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          keepalive: true,
        }).catch(() => {});
      } catch {
        // Silent fail on unload
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendBeaconHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', sendBeaconHeartbeat);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', sendBeaconHeartbeat);
    };
  }, [sessionId, apiPath, sendHeartbeat]);
}
